import React, { useCallback } from 'react';
import './draggable-entity-card.css';
import { ENTITY_COLORS, getCanvasEntityDOMId } from '../../services/editor/ui';
import { Canvas, DraggableObject } from '../../lib/canvas';
import { EditorService } from '../../services/editor';
import './variable-scope-marker.css';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import { useCanvasPopup } from '../../services/editor/CanvasPopup';
import {
	READABLE_ENTITY_TYPES,
	resolveVariableScope,
	VariableState,
	CanvasEntityDisabledReason,
  safeResolveEntityName
} from '@elyx-code/project-logic-tree';

interface IVariableScopeMarkerProps {
	draggableObject: DraggableObject;
	entity: VariableState;
	project: EditorService;
	parentDisabledReasons: CanvasEntityDisabledReason[];
}

export default function VariableScopeMarker(props: IVariableScopeMarkerProps) {
	const scope = resolveVariableScope(props.entity);

	const memoizedCanvasPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				containerClasses={['tooltip', 'tooltip--large-font']}
			>
				{scope === 'global-declaration' || scope === 'global-instance'
					? 'This is a globally scoped variable. The value it holds is shared throught the project'
					: `This is a variable used only in the scope of the ${safeResolveEntityName(
							props.entity.parent,
							props.project.logic
						)} ${READABLE_ENTITY_TYPES[props.entity.parent.type].singular}`}
			</CanvasPopupBaseComponent>
		),
		[props.entity, props.project, scope]
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id: 'canvas--variable-scope-marker--' + props.entity.id,
			arrowClasses: ['tooltip-arrow'],
			anchorQuerySelector:
				'#' +
				getCanvasEntityDOMId(props.entity) +
				'--variable-scope-marker',
			placement: 'top',
			parentCanvasObject: props.draggableObject
		},
		memoizedCanvasPopupBody
	)
		.setupHover({
			closeDelay: 80,
			openDelay: 600
		})
		.onBeforeShow(() => scope !== 'local-instance');

	const entity = props.entity;
	const entityColor = ENTITY_COLORS[entity.type];

	const resolveVariableScopeMarkerLabel = (
		scope:
			| 'global-declaration'
			| 'global-instance'
			| 'local-declaration'
			| 'local-instance'
	) => {
		switch (scope) {
			case 'global-declaration':
				return 'NEW GLOBAL';
			case 'global-instance':
				return 'GLOBAL';
			case 'local-declaration':
				return 'NEW';
			case 'local-instance':
				return '';
			default:
				return '';
		}
	};

	/*
		There are a few options for the scope of the variable
		If this variable is a instance of a local variable, we don't need to show the scope marker
		If it is an instance of a global variable, we show 'GLOBAL' in the marker
		If it is the declaration of a global variable, we show 'GLOBAL' in the marker
		If it is the declaration of a local variable, we show 'NEW' in the marker
	*/
	return (
		scope !== 'local-instance' && (
			<span
				className={[
					'variable-scope-marker',
					...(!!props.parentDisabledReasons.length
						? ['disabled']
						: [])
				].join(' ')}
				ref={canvasPopup.anchorRef}
				id={`${getCanvasEntityDOMId(props.entity)}--variable-scope-marker`}
				{...{
					[Canvas.OBJ_ATTR_NAME]: `${getCanvasEntityDOMId(
						props.entity
					)}--variable-scope-marker`
				}}
				style={{
					background: `linear-gradient(to bottom, ${entityColor} 0%, ${entityColor} 50%, transparent 50%, transparent 100%)`
				}}
			>
				{resolveVariableScopeMarkerLabel(scope)}
			</span>
		)
	);
}
